import { toGenericPriceKey, toOracleKey } from '../../assets'
import { GenericTokenList } from '../../types'
import { parseRawAmount } from '../../utils/parsing'
import { composeVaultDisplayName } from '../displayName'
import {
  fetchLagoonApiVaults,
  hasLagoonVaults,
  type LagoonApiVault,
} from './api'
import { LagoonVault, LagoonVaults } from './types'

const BRAND = 'Lagoon'

/** Pick the headline APR from the available windows. Monthly is the
 *  default (stable but responsive); fall back through weekly → yearly →
 *  inception when a window has no history yet. Values are already in
 *  percent (e.g. 10.37 = 10.37 %). */
const pickApr = (
  apr: LagoonVault['apr'],
): { rate: number; window: LagoonVault['aprWindow'] } => {
  if (apr.monthly != null) return { rate: apr.monthly, window: 'monthly' }
  if (apr.weekly != null) return { rate: apr.weekly, window: 'weekly' }
  if (apr.yearly != null) return { rate: apr.yearly, window: 'yearly' }
  if (apr.inception != null) return { rate: apr.inception, window: 'inception' }
  return { rate: 0, window: 'none' }
}

const num = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

function parseVault(
  v: LagoonApiVault,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
): LagoonVault | null {
  const address = (v?.address ?? '').toLowerCase()
  const assetAddr = (v?.asset?.address ?? '').toLowerCase()
  if (!address || !assetAddr) return null

  const state = v.state
  // A vault with no settled state carries no usable NAV/supply — skip
  // it rather than emit a zero-supply row the user route can't price.
  if (!state) return null

  const assetMeta = tokenList[assetAddr]
  const decimals = Number(v.decimals ?? 18)
  const assetDecimals = Number(v.asset?.decimals ?? assetMeta?.decimals ?? 18)

  const totalAssetsRaw = state.totalAssets?.toString() ?? '0'
  const totalSupplyRaw = state.totalSupply?.toString() ?? '0'
  const totalAssetsFormatted = Number(
    parseRawAmount(totalAssetsRaw, assetDecimals),
  )

  const apr: LagoonVault['apr'] = {
    weekly: num(state.weeklyApr?.twrrNetApr),
    monthly: num(state.monthlyApr?.twrrNetApr),
    yearly: num(state.yearlyApr?.twrrNetApr),
    inception: num(state.inceptionApr?.twrrNetApr),
  }
  const { rate: supplyRate, window: aprWindow } = pickApr(apr)

  // Underlying USD: prefer our price map (oracle-keyed, consistent with
  // the rest of the pipeline); fall back to the API's own valuation
  // implied by totalAssetsUsd / totalAssets.
  const oracleKey =
    toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(assetAddr, chainId)
  const apiTotalAssetsUsd = num(state.totalAssetsUsd) ?? 0
  const impliedPriceUsd =
    totalAssetsFormatted > 0 ? apiTotalAssetsUsd / totalAssetsFormatted : 0
  const priceUsd = prices[oracleKey] ?? prices[assetAddr] ?? impliedPriceUsd

  const totalAssetsUsd = apiTotalAssetsUsd || totalAssetsFormatted * priceUsd

  const name = (v.name ?? '').trim()
  const symbol = (v.symbol ?? '').trim()
  // Lagoon API names are already curated and human-readable
  // ("Tulipa USDC"), so use them directly; fall back to the generic
  // composer only when the API name is empty.
  const displayName =
    name || composeVaultDisplayName(BRAND, undefined, assetMeta, symbol)

  // Lagoon's API exposes the curator(s) directly; first entry is primary.
  const curatorName = v.curators?.find((c) => c?.name)?.name ?? undefined

  return {
    address,
    underlying: assetAddr,
    symbol,
    name: name || symbol,
    displayName,
    ...(curatorName ? { curatorName } : {}),
    decimals,
    assetDecimals,
    totalAssets: totalAssetsRaw,
    totalSupply: totalSupplyRaw,
    pricePerShare: state.pricePerShare?.toString() ?? '0',
    pricePerShareUsd: num(state.pricePerShareUsd) ?? undefined,
    supplyRate,
    rewardsRate: 0,
    depositRate: supplyRate,
    aprWindow,
    apr,
    isAsyncOnly: state.isAsyncOnly ?? false,
    syncMode: state.syncMode ?? 'Async',
    asset: assetMeta,
    priceUsd,
    totalAssetsFormatted,
    totalAssetsUsd,
  }
}

/**
 * One-shot fetcher for all visible Lagoon vaults on a chain.
 *
 * HTTP-only (Lagoon public GraphQL API) — no multicall executor needed,
 * mirroring the Morpho fetcher. Returns an empty map for chains without
 * a Lagoon deployment.
 *
 * @param chainId    target chain
 * @param prices     optional price map keyed by oracle key / underlying
 * @param tokenList  optional token list for hydrating `asset` metadata
 *
 * @returns `LagoonVaults` — map keyed by lowercased vault address.
 */
export const fetchLagoonVaults = async (
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): Promise<LagoonVaults> => {
  if (!hasLagoonVaults(chainId)) return {}

  const items = await fetchLagoonApiVaults(chainId)
  const out: LagoonVaults = {}
  for (const item of items) {
    const parsed = parseVault(item, chainId, prices, tokenList)
    if (parsed) out[parsed.address] = parsed
  }
  return out
}
